/**
 * Where the front this app is replacing still lives, and how to get there.
 *
 * Two profiles have no workspace here — the checkout plan and the driver — and
 * both are fully served by the old front, so they are handed over to it instead
 * of being shown a dead end. See `legacyProfileOf` in `./authSelectors`.
 */

/**
 * Production. Deliberately **not** `app.ninjahubs.cl`, which is the old front
 * today: the only reading of `app1` is that this app is going to take the
 * `app` hostname. Point any fallback at `app.ninjahubs.cl` and the day that
 * swap lands, the page redirects to itself forever.
 */
const DEFAULT_LEGACY_FRONT = 'https://app1.ninjahubs.cl';

/**
 * Keyed by the host **this** app is being served from, not by the one we are
 * going to. Anything missing falls through to production, which is right
 * because the only host that can still be missing is this app's own production
 * one — it does not exist yet.
 *
 * `qa-app2` → `qa` and `app` → `app1` are irregular in opposite directions, so
 * there is no rule to derive here. Two entries, written out.
 */
const LEGACY_FRONT_BY_HOST = {
  localhost: 'http://localhost:3000',
  '127.0.0.1': 'http://localhost:3000',
  '[::1]': 'http://localhost:3000',
  '0.0.0.0': 'http://localhost:3000',
  'qa-app2.ninjahubs.cl': 'https://qa.ninjahubs.cl',
};

/** The root, whatever anyone wrote — same normalising as `s3Utils`. */
const asRoot = (url) => `${String(url).replace(/\/+$/, '')}/`;

/**
 * The old front's address, from the point of view of a page being served right
 * now.
 *
 * **The host wins over the build-time variable**, which is the opposite of what
 * this codebase does everywhere else, and on purpose. `.env` is gitignored, so
 * a variable-only design is invisible in review and correct only on whichever
 * machine produced the artifact. And the obvious repair — committing
 * `.env.production` — is worse: staging and production are both `npm run
 * build`, so that file would bake the production address into the staging
 * bundle. Reading the host at runtime makes one artifact correct wherever it is
 * served. `REACT_APP_LEGACY_URL` stays as the escape hatch for a host this map
 * has not heard of.
 *
 * Returns `null` when the answer is our own origin. Both fronts are
 * `react-scripts` and both want port 3000, so whoever starts second gets 3001 —
 * and a page that redirects to itself never stops. A `null` is the screen's cue
 * to explain itself instead of navigating.
 *
 * @param {object} params
 * @param {string} params.hostname - `window.location.hostname`.
 * @param {string} [params.origin] - `window.location.origin`, to compare against.
 * @param {string} [params.envUrl] - `process.env.REACT_APP_LEGACY_URL`.
 * @returns {string|null}
 */
export const resolveLegacyFrontUrl = ({ hostname, origin, envUrl }) => {
  const target = asRoot(LEGACY_FRONT_BY_HOST[hostname] || envUrl || DEFAULT_LEGACY_FRONT);

  if (origin && target === asRoot(origin)) return null;

  return target;
};

/** @returns {string|null} The address for this page. */
export const legacyFrontUrl = () =>
  resolveLegacyFrontUrl({
    hostname: window.location.hostname,
    origin: window.location.origin,
    envUrl: process.env.REACT_APP_LEGACY_URL,
  });

/**
 * Leaves for the old front, replacing this entry so Back does not come straight
 * back into a page that would only bounce again.
 *
 * A function rather than an inline `window.location.replace` so a test has
 * something to mock — jsdom refuses real navigation and logs about it.
 */
export const goToLegacyFront = () => {
  const url = legacyFrontUrl();
  if (url) window.location.replace(url);
};
